import { supabase } from '../lib/supabase';

export const adminService = {
  // Platform Metrics
  async getPlatformMetrics() {
    try {
      const { data, error } = await supabase?.rpc('get_platform_metrics');
      if (error) throw error;
      
      return {
        totalUsers: data?.[0]?.total_users || 0,
        newSignupsToday: data?.[0]?.new_signups_today || 0,
        activeUsersWeek: data?.[0]?.active_users_week || 0,
        totalBlogViews: data?.[0]?.total_blog_views || 0,
        totalPointsDistributed: data?.[0]?.total_points_distributed || 0,
        pendingRedemptions: data?.[0]?.pending_redemptions || 0
      };
    } catch (error) {
      console.error('Error fetching platform metrics:', error);
      throw error;
    }
  },

  // User Activity Tracking
  async getAllUsers(filters = {}) {
    try {
      let query = supabase?.from('user_profiles')?.select(`
        *,
        user_activity_summary (
          total_transactions,
          total_budgets,
          total_goals,
          total_blog_views,
          total_points_earned,
          last_login_at,
          is_active
        )
      `);

      if (filters?.role) {
        query = query?.eq('role', filters?.role);
      }

      if (filters?.search) {
        query = query?.or(`email.ilike.%${filters?.search}%,full_name.ilike.%${filters?.search}%`);
      }

      query = query?.order('created_at', { ascending: false });

      const { data, error } = await query;
      if (error) throw error;

      return data?.map(user => ({
        id: user?.id,
        email: user?.email,
        fullName: user?.full_name,
        avatarUrl: user?.avatar_url,
        role: user?.role,
        plan: user?.plan,
        subscriptionStatus: user?.subscription_status,
        createdAt: user?.created_at,
        totalTransactions: user?.user_activity_summary?.total_transactions || 0,
        totalBudgets: user?.user_activity_summary?.total_budgets || 0,
        totalGoals: user?.user_activity_summary?.total_goals || 0,
        totalBlogViews: user?.user_activity_summary?.total_blog_views || 0,
        totalPointsEarned: user?.user_activity_summary?.total_points_earned || 0,
        lastLoginAt: user?.user_activity_summary?.last_login_at,
        isActive: user?.user_activity_summary?.is_active ?? true
      })) || [];
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  },

  async getUserActivityDetails(userId) {
    try {
      const { data, error } = await supabase?.from('user_activity_summary')?.select('*')?.eq('user_id', userId)?.single();
      if (error) throw error;

      return {
        userId: data?.user_id,
        totalTransactions: data?.total_transactions || 0,
        totalBudgets: data?.total_budgets || 0,
        totalGoals: data?.total_goals || 0,
        totalBlogViews: data?.total_blog_views || 0,
        totalPointsEarned: data?.total_points_earned || 0,
        lastLoginAt: data?.last_login_at,
        accountCreatedAt: data?.account_created_at,
        isActive: data?.is_active ?? true
      };
    } catch (error) {
      console.error('Error fetching user activity details:', error);
      throw error;
    }
  },

  // Blog Content Management
  async getAllBlogArticles() {
    try {
      const { data, error } = await supabase?.from('blog_articles')?.select(`
        *,
        user_profiles (full_name)
      `)?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(article => ({
        id: article?.id,
        authorId: article?.author_id,
        authorName: article?.user_profiles?.full_name || 'Rovyn Group',
        title: article?.title,
        slug: article?.slug,
        excerpt: article?.excerpt,
        topic: article?.topic,
        status: article?.status,
        viewCount: article?.view_count || 0,
        publishedAt: article?.published_at,
        createdAt: article?.created_at,
        comingSoon: article?.coming_soon || false,
        featured: article?.featured || false
      })) || [];
    } catch (error) {
      console.error('Error fetching blog articles:', error);
      throw error;
    }
  },

  async updateArticleStatus(articleId, status, adminId) {
    try {
      const updateData = { status };
      if (status === 'published') {
        updateData.published_at = new Date()?.toISOString();
      }

      const { data, error } = await supabase?.from('blog_articles')?.update(updateData)?.eq('id', articleId)?.select()?.single();

      if (error) throw error;

      // Log admin activity
      await this.logAdminActivity(
        adminId,
        status === 'published' ? 'blog_publish' : 'blog_archive',
        'blog_article',
        articleId,
        `Article ${status}: ${data?.title}`
      );

      return {
        id: data?.id,
        status: data?.status,
        publishedAt: data?.published_at
      };
    } catch (error) {
      console.error('Error updating article status:', error);
      throw error;
    }
  },

  async deleteArticle(articleId, adminId) {
    try {
      const { error } = await supabase?.from('blog_articles')?.delete()?.eq('id', articleId);
      if (error) throw error;

      // Log admin activity
      await this.logAdminActivity(
        adminId,
        'blog_archive',
        'blog_article',
        articleId,
        'Article deleted'
      );
    } catch (error) {
      console.error('Error deleting article:', error);
      throw error;
    }
  },

  // Rewards System Management
  async getAllRedemptions(status = null) {
    try {
      let query = supabase?.from('user_redemptions')?.select(`
        *,
        user_profiles (email, full_name),
        redemption_options (title, point_cost, benefit_type)
      `)?.order('redeemed_at', { ascending: false });

      if (status) {
        query = query?.eq('status', status);
      }

      const { data, error } = await query;
      if (error) throw error;

      return data?.map(redemption => ({
        id: redemption?.id,
        userId: redemption?.user_id,
        userEmail: redemption?.user_profiles?.email,
        userName: redemption?.user_profiles?.full_name,
        rewardTitle: redemption?.redemption_options?.title,
        pointsSpent: redemption?.points_spent,
        benefitType: redemption?.redemption_options?.benefit_type,
        status: redemption?.status,
        redeemedAt: redemption?.redeemed_at,
        completedAt: redemption?.completed_at,
        notes: redemption?.notes
      })) || [];
    } catch (error) {
      console.error('Error fetching redemptions:', error);
      throw error;
    }
  },

  async updateRedemptionStatus(redemptionId, status, adminId, notes = null) {
    try {
      const updateData = { status };
      if (status === 'completed') {
        updateData.completed_at = new Date()?.toISOString();
      }
      if (notes) {
        updateData.notes = notes;
      }

      const { data, error } = await supabase?.from('user_redemptions')?.update(updateData)?.eq('id', redemptionId)?.select()?.single();

      if (error) throw error;

      // Log admin activity
      await this.logAdminActivity(
        adminId,
        status === 'completed' ? 'redemption_approve' : 'redemption_reject',
        'redemption',
        redemptionId,
        `Redemption ${status}${notes ? ': ' + notes : ''}`
      );

      return {
        id: data?.id,
        status: data?.status,
        completedAt: data?.completed_at
      };
    } catch (error) {
      console.error('Error updating redemption status:', error);
      throw error;
    }
  },

  async adjustUserPoints(userId, points, reason, adminId) {
    try {
      const { error } = await supabase?.rpc('award_points', {
        p_user_id: userId,
        p_points: points,
        p_category: 'bonus',
        p_description: `Admin adjustment: ${reason}`,
        p_reference_id: adminId,
        p_reference_table: 'admin_activity_logs'
      });

      if (error) throw error;

      // Log admin activity
      await this.logAdminActivity(
        adminId,
        'point_adjustment',
        'point_transaction',
        userId,
        `Adjusted ${points} points: ${reason}`
      );
    } catch (error) {
      console.error('Error adjusting user points:', error);
      throw error;
    }
  },

  async getRewardOptions() {
    try {
      const { data, error } = await supabase?.from('redemption_options')?.select('*')?.order('display_order', { ascending: true });

      if (error) throw error;

      return data?.map(option => ({
        id: option?.id,
        title: option?.title,
        description: option?.description,
        pointCost: option?.point_cost,
        benefitType: option?.benefit_type,
        benefitValue: option?.benefit_value,
        requiresPro: option?.requires_pro,
        isActive: option?.is_active,
        displayOrder: option?.display_order
      })) || [];
    } catch (error) {
      console.error('Error fetching reward options:', error);
      throw error;
    }
  },

  async updateRewardOption(rewardId, updates, adminId) {
    try {
      const { data, error } = await supabase?.from('redemption_options')?.update({
        title: updates?.title,
        description: updates?.description,
        point_cost: updates?.pointCost,
        benefit_value: updates?.benefitValue,
        is_active: updates?.isActive
      })?.eq('id', rewardId)?.select()?.single();

      if (error) throw error;

      // Log admin activity
      await this.logAdminActivity(
        adminId,
        'reward_config',
        'reward',
        rewardId,
        `Reward updated: ${data?.title}`
      );

      return {
        id: data?.id,
        title: data?.title,
        pointCost: data?.point_cost
      };
    } catch (error) {
      console.error('Error updating reward option:', error);
      throw error;
    }
  },

  // Admin Activity Logging
  async logAdminActivity(adminId, actionType, targetType, targetId, description, metadata = null) {
    try {
      const { error } = await supabase?.rpc('log_admin_activity', {
        p_admin_id: adminId,
        p_action_type: actionType,
        p_target_type: targetType,
        p_target_id: targetId,
        p_description: description,
        p_metadata: metadata
      });

      if (error) throw error;
    } catch (error) {
      console.error('Error logging admin activity:', error);
      // Don't throw - logging failure shouldn't break main operation
    }
  },

  async getAdminActivityLogs(limit = 50) {
    try {
      const { data, error } = await supabase?.from('admin_activity_logs')?.select(`
        *,
        user_profiles (email, full_name)
      `)?.order('created_at', { ascending: false })?.limit(limit);

      if (error) throw error;

      return data?.map(log => ({
        id: log?.id,
        adminId: log?.admin_id,
        adminEmail: log?.user_profiles?.email,
        adminName: log?.user_profiles?.full_name,
        actionType: log?.action_type,
        targetType: log?.target_type,
        targetId: log?.target_id,
        description: log?.description,
        metadata: log?.metadata,
        createdAt: log?.created_at
      })) || [];
    } catch (error) {
      console.error('Error fetching admin activity logs:', error);
      throw error;
    }
  },

  // Platform Analytics
  async getPlatformAnalytics(days = 30) {
    try {
      const { data, error } = await supabase?.from('platform_analytics')?.select('*')?.gte('metric_date', new Date(Date.now() - days * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0])?.order('metric_date', { ascending: false });

      if (error) throw error;

      return data?.map(metric => ({
        metricDate: metric?.metric_date,
        totalUsers: metric?.total_users,
        newSignups: metric?.new_signups,
        activeUsers: metric?.active_users,
        totalTransactions: metric?.total_transactions,
        totalBlogViews: metric?.total_blog_views,
        totalPointsEarned: metric?.total_points_earned,
        totalRedemptions: metric?.total_redemptions
      })) || [];
    } catch (error) {
      console.error('Error fetching platform analytics:', error);
      throw error;
    }
  }
};